import React, { useState, useRef } from 'react';
import SyncStatusIndicator from './SyncStatusIndicator';
import OrderActionButtons from './OrderActionButtons';
import { tunisianCities } from '../../../../utils/cities';
import { usePermissionViewModel } from '../../permissions/viewmodel/permission-viewmodel';
import { updateOrderAvisCommentaire } from '../../orders-controller/order-controller';

const InlineEdit = ({ value, onSave, placeholder, disabled }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try { await onSave(val); } catch {}
    setSaving(false);
    setEditing(false);
  };

  if (editing) {
    return (
      <textarea
        ref={ref}
        value={val}
        autoFocus
        rows={2}
        onChange={e => setVal(e.target.value)}
        onBlur={save}
        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); save(); } if (e.key === 'Escape') { setVal(value || ''); setEditing(false); } }}
        disabled={saving}
        className="w-full min-w-[120px] text-xs border border-emerald-400 rounded p-1 focus:outline-none focus:ring-1 focus:ring-emerald-500 resize-none"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => { if (!disabled) { setVal(value || ''); setEditing(true); } }}
      className={`text-left w-full min-w-[80px] text-xs rounded px-1 py-0.5 transition-colors ${disabled ? 'cursor-default' : 'cursor-pointer hover:bg-emerald-50 border border-transparent hover:border-emerald-200'} ${val ? 'text-gray-900' : 'text-gray-400 italic'}`}
      title={disabled ? undefined : 'Cliquer pour modifier'}
    >
      {val || placeholder}
    </button>
  );
};

const DesktopOrderRow = ({
  order,
  allowedOptions,
  statusInfo,
  isSelectableStatus,
  isSelected,
  handleOrderSelection,
  formatDate,
  getClientFullName,
  handleStatusChange,
  getStatusLabel,
  newlyAddedOrders,
  handleDeleteOrder,
  handleTrackOrder,
  handleCallStatusChange,
  onOrderUpdated = () => {}
}) => {
  const [localAvis, setLocalAvis] = useState(order.avisClient || '');
  const [localCommentaire, setLocalCommentaire] = useState(order.commentaire || '');

  const handleSaveAvis = async (val) => {
    await updateOrderAvisCommentaire(order.id, val, localCommentaire);
    setLocalAvis(val);
    onOrderUpdated();
  };

  const handleSaveCommentaire = async (val) => {
    await updateOrderAvisCommentaire(order.id, localAvis, val);
    setLocalCommentaire(val);
    onOrderUpdated();
  };

  const getCityName = (cityCode) => {
    if (!cityCode) return 'N/A';
    const city = tunisianCities.find(city => city.Code === cityCode);
    return city ? city.EnName : cityCode;
  };
  const isConfirmedStatus = ['confirmé', 'confirmed'].includes(order.etat?.toLowerCase());
  const {hasPermission} = usePermissionViewModel('commande')

  // Get phone icon color based on call status
  const getPhoneIconColor = (callStatus) => {
    if (callStatus === null) {
      return 'text-gray-400'; // Gray for null
    } else if (callStatus === true || callStatus === 1) {
      return 'text-green-500'; // Green for successful call
    } else if (callStatus === false || callStatus === 0) {
      return 'text-red-500'; // Red for failed call
    }
    return 'text-gray-400'; // Default to gray
  };

  const getPhoneIconCursor = (callStatus) => {
    return callStatus === null ? 'cursor-pointer' : 'cursor-pointer';
  };


  return (
    <tr data-order-id={order.id} className={`${isSelected ? "bg-indigo-50" : ""} ${newlyAddedOrders[order.id] ? "highlight-new-order" : ""} transition-all duration-300`}>
      <td className="px-6 py-4 whitespace-nowrap">
        {/* Show checkbox always enabled */}
        <input
        
          type="checkbox"
          className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
          checked={isSelected}
          onChange={() => handleOrderSelection(order.id, order.etat)}
        />
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm font-medium text-gray-900">#{order.id}</div>
        <div className="text-xs text-gray-500">{order.reference || 'N/A'}</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="flex-shrink-0 h-8 w-8">
            <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 font-medium">
              {order.clientFirstName ? order.clientFirstName.charAt(0).toUpperCase() : '?'}
            </div>
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">{getClientFullName(order)}</div>
            <div className="text-sm text-gray-500">{order.clientNumber || 'Pas de numéro'}</div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-center">
        <button
          onClick={() => handleCallStatusChange && handleCallStatusChange(order.id, order.call)}
          className={`p-1 rounded-full hover:bg-gray-100 transition-colors ${getPhoneIconCursor(order.call)}`}
          title={
            order.call === null 
              ? "Cliquez pour marquer comme appelé" 
              : (order.call === true || order.call === 1)
              ? "Appel réussi - Cliquez pour marquer comme échoué" 
              : "Appel échoué - Cliquez pour marquer comme réussi"
          }
        >
          <svg 
            className={`h-5 w-5 ${getPhoneIconColor(order.call)}`} 
            fill="currentColor" 
            viewBox="0 0 20 20"
          >
            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
          </svg>
        </button>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {formatDate(order.dateBonCommande)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm font-medium text-gray-900">{(order.totalTTC || 0).toFixed(2)} TND</div>
        <div className="flex flex-col text-xs text-gray-500">
          <span>HT: {(order.totalHt || 0).toFixed(2)} TND</span>
          <span>TVA: {(order.totalTva || 0).toFixed(2)} TND</span>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        <div>{order.description || 'N/A'}</div>
      </td>
      <td className="px-3 py-4" style={{ minWidth: '120px', maxWidth: '180px' }}>
        <InlineEdit
          value={localAvis}
          onSave={handleSaveAvis}
          placeholder="Avis client..."
          disabled={!hasPermission('write')}
        />
      </td>
      <td className="px-3 py-4" style={{ minWidth: '120px', maxWidth: '180px' }}>
        <InlineEdit
          value={localCommentaire}
          onSave={handleSaveCommentaire}
          placeholder="Commentaire..."
          disabled={!hasPermission('write')}
        />
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span
          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusInfo.color}`}
        >
          {statusInfo.label}
        </span>
        <div className="text-xs text-gray-500 mt-1">{getCityName(order.ville)}</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-center">
        <SyncStatusIndicator syncStatus={order.syncro} />
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex items-center justify-end space-x-2">
          {/* Status dropdown */}
          <div className="relative">
            <div className="rounded-md shadow-sm">
              <select
                disabled={!hasPermission('status')}
                id={`status-select-${order.id}`}
                value={order.etat}
                onChange={(e) => {
                  const selectedOption = allowedOptions.find(opt => opt.value === e.target.value);
                  if (selectedOption) {
                    handleStatusChange(order.id, selectedOption.value, selectedOption.key);
                  }
                }}
                className="disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed block w-auto text-sm font-medium text-gray-700 truncate border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-[#174540] focus:border-[#174540] bg-white appearance-none py-1.5 pl-3 pr-8 transition duration-150 ease-in-out"
                style={{
                  width: 'fit-content',
                  maxWidth: '150px',
                  minWidth: '100px'
                }}
              >
                <option value={order.etat}>
                  {getStatusLabel(order.etat)}
                </option>
                {allowedOptions.length > 0 ? (
                  allowedOptions
                    .filter(status => status.value !== order.etat)
                    .map((status) => (
                      <option
                        key={status.value}
                        value={status.value}
                      >
                        {status.label}
                      </option>
                    ))
                ) : (
                  <option disabled>Aucune option</option>
                )}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <svg className="h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 011.414 1.414l-4 4a1 1 01-1.414 0l-4-4a1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>


          {/* Existing action buttons */}
          <OrderActionButtons orderId={order.id} handleDeleteOrder={handleDeleteOrder} order={order} />
        </div>
      </td>
    </tr>
  );
};

export default DesktopOrderRow;